import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { LocaleProvider } from '@spacegame/ui';
import { applySettingsToDom, loadSettings, saveSettings, type Settings } from './settings';

export interface SettingsActions {
  setTheme: (theme: Settings['theme']) => void;
  setReducedMotion: (enabled: boolean) => void;
  setSkipBoostConfirm: (enabled: boolean) => void;
  setLocale: (locale: Settings['locale']) => void;
  setTutorialSeen: (seen: boolean) => void;
}

const SettingsContext = createContext<Settings | null>(null);
const SettingsActionsContext = createContext<SettingsActions | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(() => loadSettings());

  // The DOM attributes are the one place @spacegame/ui's CSS actually reads
  // theme/motion from — every change has to be mirrored there immediately,
  // not just persisted.
  useEffect(() => {
    applySettingsToDom(settings);
    saveSettings(settings);
  }, [settings]);

  const actions = useMemo<SettingsActions>(
    () => ({
      setTheme: (theme) => setSettings((prev) => ({ ...prev, theme })),
      setReducedMotion: (enabled) => setSettings((prev) => ({ ...prev, reducedMotion: enabled })),
      setSkipBoostConfirm: (enabled) => setSettings((prev) => ({ ...prev, skipBoostConfirm: enabled })),
      setLocale: (locale) => setSettings((prev) => ({ ...prev, locale })),
      setTutorialSeen: (seen) => setSettings((prev) => ({ ...prev, tutorialSeen: seen })),
    }),
    [],
  );

  return (
    <SettingsContext.Provider value={settings}>
      <SettingsActionsContext.Provider value={actions}>
        <LocaleProvider locale={settings.locale}>{children}</LocaleProvider>
      </SettingsActionsContext.Provider>
    </SettingsContext.Provider>
  );
}

export function useSettings(): Settings {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used inside <SettingsProvider>.');
  return ctx;
}

export function useSettingsActions(): SettingsActions {
  const ctx = useContext(SettingsActionsContext);
  if (!ctx) throw new Error('useSettingsActions must be used inside <SettingsProvider>.');
  return ctx;
}

